/**
 * One hardware order on a case (procurement.md §4): vendor, ship-to, PO status with "awaiting Books approval", the
 * lines with their price source, shipments, and the way into the receive screen. Owner: web-procurement.
 */
import { Button, Chip, Input, toast } from '@patchbay/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { PackageCheck, RefreshCw } from 'lucide-react'
import * as React from 'react'
import { BooksRecordLink } from '../../components/books-record-link'
import { describeError } from '../../lib/api/client'
import { formatWhen } from '../../lib/display-time'
import { poText } from '../../lib/po'
import { type Card, type HardwareOrderOut, procurementApi, procurementKeys, type SellSource } from './api'
import { ProcurementConfirmCard } from './card'
import { InstallUnit } from './install'
import { money } from './pricing'
import { ShipmentsPanel } from './shipments'
import { PoStatusChip, ProcurementChip } from './status'
import { lineDelivery } from './tracking'

export const SELL_SOURCE_LABEL: Record<SellSource, string> = {
  books_price: 'Books price',
  markup: 'Cost + markup',
  override: 'Override',
}

const CUSTOMER_BILLING_LABEL: Record<NonNullable<HardwareOrderOut['lines'][number]['customer_billing']>, string> = {
  prepared: 'Customer line prepared',
  posted: 'Customer line posted',
  not_billable: 'Customer: not billable',
  not_billable_covered: 'Customer: not billable (covered)',
  void: 'Customer line void',
}

function when(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : formatWhen(value, { style: 'absolute' })
}

/** A PO Books can still delete or cancel from under us: "Check Books now" re-reads it (the poll also does). */
const BOOKS_WATCHED: readonly string[] = ['pending_approval', 'approved', 'issued', 'partially_received', 'received']

/** A hand-raised PO can be received once the order is out with the vendor, including after it is billed (#1104). */
const MANUAL_RECEIVABLE = ['ordered', 'shipped', 'partially_received', 'received', 'installed', 'billed']

export function isFullyReceived(order: HardwareOrderOut): boolean {
  return order.lines.length > 0 && order.lines.every((l) => l.qty_received >= l.qty)
}

export function canReceive(order: HardwareOrderOut): boolean {
  if (!order.lines.some((line) => line.qty_received < line.qty)) return false
  if (order.status === 'cancelled' || order.status === 'rejected') return false
  const po = order.purchase_order?.status
  if (!po) return order.po_source === 'manual' && MANUAL_RECEIVABLE.includes(order.status)
  // Billing state does not hide an unreceived line. Draft, pending, rejected and cancelled still do.
  return po !== 'draft' && po !== 'pending_approval' && po !== 'rejected' && po !== 'cancelled'
}

function UnitDetails({ unit, order }: { unit: NonNullable<HardwareOrderOut['units']>[number]; order: HardwareOrderOut }) {
  const qc = useQueryClient()
  const [editing, setEditing] = React.useState(false)
  const [serial, setSerial] = React.useState(unit.serial ?? '')
  const [model, setModel] = React.useState(unit.model ?? '')
  const [mac, setMac] = React.useState(unit.mac ?? '')
  const [saving, setSaving] = React.useState(false)
  const [card, setCard] = React.useState<Card | null>(null)
  if (card) {
    return (
      <ProcurementConfirmCard
        card={card}
        onChange={setCard}
        onSettled={() => qc.invalidateQueries({ queryKey: procurementKeys.case(order.case_id) })}
      >
        <p>
          Serial {serial || '(unchanged)'} · model {model || '(unchanged)'} · MAC {mac || '(unchanged)'}
        </p>
      </ProcurementConfirmCard>
    )
  }
  if (!editing) {
    return !unit.serial ? (
      <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
        Record serial
      </Button>
    ) : null
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Input
        aria-label="Unit serial"
        placeholder="Serial"
        className="h-7 w-32"
        value={serial}
        onChange={(event) => setSerial(event.currentTarget.value)}
      />
      <Input
        aria-label="Unit model"
        placeholder="Model"
        className="h-7 w-28"
        value={model}
        onChange={(event) => setModel(event.currentTarget.value)}
      />
      <Input
        aria-label="Unit MAC"
        placeholder="MAC"
        className="h-7 w-32"
        value={mac}
        onChange={(event) => setMac(event.currentTarget.value)}
      />
      <Button
        size="sm"
        disabled={saving || (!serial.trim() && !model.trim() && !mac.trim())}
        onClick={async () => {
          setSaving(true)
          try {
            setCard(
              await procurementApi.updateUnit(unit.id, {
                serial: serial.trim() || null,
                model: model.trim() || null,
                mac: mac.trim() || null,
              }),
            )
            setEditing(false)
          } catch (error) {
            toast.error(`Unit card not created: ${describeError(error)}`)
          } finally {
            setSaving(false)
          }
        }}
      >
        Save
      </Button>
    </span>
  )
}

/** "PO to be raised in Books": the number a person raised by hand, entered (or corrected) here. */
export function ManualPoSection({ order }: { order: HardwareOrderOut }) {
  const qc = useQueryClient()
  const [value, setValue] = React.useState(order.manual_po_number ?? '')
  const [editing, setEditing] = React.useState(!order.manual_po_number)
  const [saving, setSaving] = React.useState(false)
  const save = async () => {
    const number = value.trim()
    if (!number || saving) return
    setSaving(true)
    try {
      await procurementApi.recordManualPo(order.id, number)
      await qc.invalidateQueries({ queryKey: procurementKeys.case(order.case_id) })
      toast(`${poText(number)} recorded on the order`)
      setEditing(false)
    } catch (error) {
      toast.error(`PO number not saved: ${describeError(error)}`)
    } finally {
      setSaving(false)
    }
  }
  return (
    <section className="space-y-1.5 text-sm" aria-label="Purchase order">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={order.manual_po_number ? 'outline' : 'warning'}>
          {order.manual_po_number ? poText(order.manual_po_number) : 'PO to be raised in Books'}
        </Chip>
        <span className="text-xs text-muted-foreground">
          {order.po_state_text ?? 'Raised by hand in Books: Patchbay does not create this PO.'}
        </span>
        {!editing && order.status !== 'cancelled' && order.status !== 'rejected' ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            Change PO number
          </Button>
        ) : null}
      </div>
      {editing && order.status !== 'draft' ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            aria-label="PO number from Books"
            placeholder="PO number from Books"
            className="h-8 w-48"
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
          />
          <Button size="sm" disabled={!value.trim() || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save PO number'}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function PoNumber({ order }: { order: HardwareOrderOut }) {
  const po = order.purchase_order
  const number = poText(po?.books_po_number) || 'PO (number not issued yet)'
  return (
    <BooksRecordLink href={po?.books_url} label={`Open ${number} in Books`}>
      {number}
    </BooksRecordLink>
  )
}

export function OrderCard({ order, showHeading = true }: { order: HardwareOrderOut; showHeading?: boolean }) {
  const qc = useQueryClient()
  const [checking, setChecking] = React.useState(false)
  const po = order.purchase_order
  const currency = po?.currency ?? 'CAD'

  const refresh = async () => {
    if (!po) return
    setChecking(true)
    try {
      const out = await procurementApi.refreshPo(po.id)
      await qc.invalidateQueries({ queryKey: procurementKeys.case(order.case_id) })
      toast(
        out.status === 'cancelled'
          ? out.books_status === 'deleted'
            ? 'Books says this PO was deleted'
            : 'Books says this PO was cancelled'
          : out.status === 'pending_approval'
            ? 'Still awaiting approval in Books'
            : `Books says: ${out.status.replaceAll('_', ' ')}`,
      )
    } catch (error) {
      toast.error(`Books could not be checked: ${describeError(error)}`)
    } finally {
      setChecking(false)
    }
  }

  return (
    <article
      id={`hardware-${order.id}`}
      className="space-y-3 rounded-lg border border-border bg-card p-3"
      data-order={order.id}
      aria-label={`Hardware order from ${order.vendor_name ?? 'vendor'}`}
    >
      {showHeading ? (
        <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="min-w-0 font-semibold break-words">{order.vendor_name ?? 'Vendor name unavailable'}</h3>
          <ProcurementChip status={order.procurement_status} />
          <span className="basis-full text-xs text-muted-foreground">
            {order.ship_to.kind === 'office'
              ? 'Ship to our office'
              : `Drop-ship to site${order.ship_to.address ? `: ${order.ship_to.address}` : ''}`}
            {order.needed_by ? ` · needed by ${order.needed_by}` : ''}
          </span>
        </header>
      ) : null}

      {order.po_source === 'manual' && !po ? (
        <ManualPoSection order={order} />
      ) : (
        <section className="flex flex-wrap items-center gap-2 text-sm" aria-label="Purchase order">
          {po ? (
            <>
              <PoNumber order={order} />
              <PoStatusChip status={po.status} errorCode={po.error_code} />
              {po.amount !== undefined ? <span className="tabular text-muted-foreground">{money(po.amount ?? null, currency)}</span> : null}
              {po.status === 'pending_approval' ? (
                <span className="text-xs text-muted-foreground">
                  Submitted {when(po.submitted_at) ?? '(time unknown)'}; the vendor order goes out once Books approves it.
                </span>
              ) : null}
              {BOOKS_WATCHED.includes(po.status) ? (
                <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={checking}>
                  <RefreshCw /> Check Books now
                </Button>
              ) : null}
              {po.approved_at ? (
                <span className="text-xs text-muted-foreground">
                  Approved {when(po.approved_at)}
                  {po.approved_by_books ? ` by ${po.approved_by_books} in Books` : ' in Books'}
                </span>
              ) : null}
              {po.status === 'rejected' ? (
                <span className="text-xs text-destructive">Rejected in Books: {po.rejected_reason || 'no reason recorded'}</span>
              ) : null}
              {po.reconciliation_state === 'mismatch' ? (
                <Chip tone="warning">Desk PO differs: {(po.reconciliation_mismatches ?? []).join(', ')}</Chip>
              ) : null}
              {po.reconciliation_state === 'unreachable' ? <Chip tone="warning">Desk PO comparison unreachable</Chip> : null}
            </>
          ) : (
            <Chip tone="outline">No purchase order yet (order card not confirmed)</Chip>
          )}
        </section>
      )}

      <ul className="divide-y divide-border text-sm" aria-label="Order lines">
        {order.lines.map((l) => {
          const delivery = lineDelivery(order, l)
          return (
            <li key={l.id} className="space-y-0.5 py-1.5" data-line={l.id}>
              <p className="font-medium break-words">{l.product_name}</p>
              <p className="text-xs break-words text-muted-foreground">
                {[l.sku, l.model].filter(Boolean).join(' · ') || 'no SKU'}
                {l.is_serialized ? ' · serial-tracked' : ''}
                {l.unit_cost !== undefined ? ` · cost ${money(l.unit_cost ?? null, currency)}` : ''}
              </p>
              <p className="flex flex-wrap gap-x-3 text-xs">
                <span className="tabular" data-receipt={l.id}>
                  {delivery.primary}
                </span>
                {delivery.detail ? <span data-delivery-note>{delivery.detail}</span> : null}
                <span>
                  Sell{' '}
                  {l.unit_sell !== undefined ? <span className="tabular">{money(l.unit_sell ?? null, currency)} each</span> : 'withheld'}{' '}
                  <span className="text-muted-foreground">({SELL_SOURCE_LABEL[l.sell_source]})</span>
                </span>
              </p>
              {l.customer_billing ? (
                <p className="text-xs text-muted-foreground" data-customer-billing={l.customer_billing}>
                  {CUSTOMER_BILLING_LABEL[l.customer_billing]}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>

      {isFullyReceived(order) ? (
        <p className="text-xs text-muted-foreground" data-received>
          {order.procurement_status === 'staged' || order.staged_at ? 'Staged at the office' : 'Received'}
          {order.procurement_status === 'staged' || order.staged_at
            ? order.staged_at
              ? ` ${when(order.staged_at)}`
              : ''
            : order.received_at
              ? ` ${when(order.received_at)}`
              : ''}
        </p>
      ) : null}

      <ShipmentsPanel order={order} />

      {order.units?.length ? (
        <section aria-label="Received units">
          <h4 className="text-xs font-semibold tracking-wide text-subtle uppercase">Units</h4>
          <ul className="mt-1 space-y-2 text-xs">
            {order.units.map((u) => (
              <li key={u.id} className="space-y-1" data-unit={u.id}>
                <dl className="grid grid-cols-[4.5rem_1fr] gap-x-2 gap-y-0.5">
                  <dt className="text-muted-foreground">Model</dt>
                  <dd className="break-words">{u.model ?? 'Not recorded'}</dd>
                  <dt className="text-muted-foreground">Serial</dt>
                  <dd className="break-words">{u.serial ? <span className="font-mono">{u.serial}</span> : 'Not recorded'}</dd>
                  <dt className="text-muted-foreground">MAC</dt>
                  <dd className="break-words">{u.mac ? <span className="font-mono">{u.mac}</span> : 'Not recorded'}</dd>
                  <dt className="text-muted-foreground">Where</dt>
                  <dd>{u.location === 'office' ? 'At the office' : 'On site'}</dd>
                </dl>
                <div className="flex flex-wrap items-center gap-2">
                  <InstallUnit unit={u} caseId={order.case_id} orderId={order.id} />
                  <UnitDetails unit={u} order={order} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canReceive(order) ? (
        <Link
          to="/receive/$orderId"
          params={{ orderId: order.id }}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border-strong bg-card px-3 text-sm font-medium hover:bg-muted"
        >
          <PackageCheck className="size-4" /> Receive and record serials
        </Link>
      ) : null}
    </article>
  )
}

/**
 * Orders that are dead: cancelled here, or whose PO was cancelled or deleted in Books. Collapsed under one line and
 * struck through so they stop reading as parts on the way (patchbay#446).
 */
export function CancelledOrders({ orders }: { orders: HardwareOrderOut[] }) {
  return (
    <details className="rounded-lg border border-border bg-card p-2 text-sm" data-cancelled-orders>
      <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Cancelled orders ({orders.length})</summary>
      <ul className="mt-2 space-y-1.5">
        {orders.map((order) => {
          const po = order.purchase_order
          const why = po?.books_status === 'deleted' ? 'deleted in Books' : po ? 'cancelled' : 'cancelled'
          return (
            <li key={order.id} className="flex flex-wrap items-center gap-x-2 text-xs" data-order={order.id}>
              <span className="text-muted-foreground line-through">{order.vendor_name ?? 'Vendor'}</span>
              {po?.books_po_number ? (
                <span className="line-through">
                  <BooksRecordLink
                    href={po.books_url}
                    label={`Open ${poText(po.books_po_number)} in Books`}
                    className="font-medium text-muted-foreground line-through"
                  >
                    {poText(po.books_po_number)}
                  </BooksRecordLink>
                </span>
              ) : null}
              <span className="text-muted-foreground">{why}</span>
            </li>
          )
        })}
      </ul>
    </details>
  )
}
